import { useCallback, useMemo } from "react";

import useTimer from "@repo/shopify/hooks/useTimer";

import { EARLY_RELEASE_END, EARLY_RELEASE_START } from "@const/constant";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";

const DISCOUNT_TAG_REGEX = /^WEATHER_GEAR_(?<rate>\d+)%$/;

export default function useEarlyRelease() {
  const { watch } = useTimer({ type: "increase" });
  const { token } = useCustomerStore();
  const { lang } = useLanguage();

  const isEarlyReleasePeriod = useMemo(() => watch >= EARLY_RELEASE_START && watch <= EARLY_RELEASE_END, [watch]);

  const isEligible = useMemo(
    () => isEarlyReleasePeriod && Boolean(token.accessToken) && lang !== "jp",
    [isEarlyReleasePeriod, token.accessToken, lang],
  );

  // 상품 tags에 WEATHER_GEAR_XX% 태그가 있으면 price에 할인율 적용한 결과를 반환, 없거나 비대상자면 null
  const getDiscountedPrice = useCallback(
    (tags: string[] | undefined, price: number): { price: number; compareAtPrice: number } | null => {
      if (!isEligible || !tags) return null;

      const discountTag = tags.find((tag) => DISCOUNT_TAG_REGEX.test(tag));
      if (!discountTag) return null;

      const match = DISCOUNT_TAG_REGEX.exec(discountTag);
      const discountRateText = match?.groups?.rate;
      if (!discountRateText) return null;

      const discountRate = parseInt(discountRateText, 10);
      const discountedPrice = Math.floor(price * (1 - discountRate / 100));

      return { price: discountedPrice, compareAtPrice: price };
    },
    [isEligible],
  );

  return { isEligible, isEarlyReleasePeriod, getDiscountedPrice };
}
